import { type FC, memo, useMemo, useState } from 'react';
import type { Store } from '../../data/store';
import type { InventoryValue } from '../../data/store-stats';
import { shortCurrencyFormatter } from '../../helpers/format-currency';
import { formatUnitsQuantity } from '../../helpers/format-quantity';
import { SegmentedToggle } from '../segmented-toggle';
import { thisYearColor } from './constants';
import {
  groupValueHistoryByYear,
  type InventoryValueBasis,
  type InventoryValueMetric,
} from './helpers';
import { SummaryHeader } from './summaries';
import { WidgetContainer } from './widget-container';
import {
  priorYearColor,
  seriesBounds,
  YearOverYearChart,
  yearLineSeries,
} from './year-over-year-chart';

const TITLE = 'Inventory Value';

const formatMoney = (value: number | null) =>
  value === null ? 'N/A' : shortCurrencyFormatter(value);

const formatUnits = (value: number | null) => (value === null ? 'N/A' : formatUnitsQuantity(value));

type MetricChartProperties = {
  history: InventoryValue[];
  metric: InventoryValueMetric;
  hideLegend?: boolean;
};

// MetricChart plots one measure of the same snapshots: this year in the
// dashboard's current-year green, earlier years in the sales widgets' fading
// blue. Memoized, so switching cost/retail leaves the units chart below it
// alone — none of its inputs changed.
const MetricChart = memo(({ history, metric, hideLegend }: MetricChartProperties) => {
  // The formatter follows from the metric instead of riding alongside it, so
  // no caller can label a bottle count in dollars.
  const format = metric === 'qty_on_hand' ? formatUnits : formatMoney;

  const series = useMemo(
    () =>
      groupValueHistoryByYear(history, metric).map(({ year, monthly }, index) =>
        yearLineSeries(
          year,
          monthly,
          index === 0 ? thisYearColor : priorYearColor(index - 1),
          format
        )
      ),
    [history, metric, format]
  );

  const yAxis = useMemo(
    () => ({ ...seriesBounds(series), valueFormatter: format }),
    [series, format]
  );

  // No height: the two charts share whatever the card has left over.
  return <YearOverYearChart series={series} yAxis={yAxis} hideLegend={hideLegend} />;
});

// InventoryValueDisplay charts the store's month-end inventory, year over year:
// what it is worth on top, at cost or retail, and how many units it took to be
// worth that underneath. The two together say whether a change in value came
// from the shelves or from the prices on them.
//
// Lines only, and the axis zooms to the data: inventory is a stock level that
// moves a few percent a month, so zero-based bars would read as flat.
export const InventoryValueDisplay: FC<{ store: Store }> = ({ store }) => {
  const [basis, setBasis] = useState<InventoryValueBasis>('cost');
  const history = store.stats.inventory.value_history;

  const toggle = (
    <SegmentedToggle<InventoryValueBasis>
      value={basis}
      onChange={setBasis}
      className="[&_button]:px-4 [&_button]:py-1 [&_button]:leading-5"
      options={[
        { value: 'cost', label: 'Cost' },
        { value: 'retail', label: 'Retail' },
      ]}
    />
  );

  if (history.length === 0) {
    return (
      <WidgetContainer title={TITLE} action={toggle}>
        <p className="p-4 text-gray-600">
          Your inventory value will start appearing after your next upload.
        </p>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer title={TITLE} action={toggle}>
      {/* Direct children of the card's flex column, so the two charts split
          its leftover height evenly. */}
      <SummaryHeader>{basis === 'cost' ? 'Value at cost' : 'Value at retail'}</SummaryHeader>
      <MetricChart history={history} metric={basis} hideLegend />
      <SummaryHeader>Units on hand</SummaryHeader>
      {/* Both charts carry the same years, so one legend serves the card. It
          sits under the lower chart, where the sales widgets put theirs. */}
      <MetricChart history={history} metric="qty_on_hand" />
    </WidgetContainer>
  );
};
